import React from 'react';
import { connect } from 'react-redux';

import {
    IReduxState,
    StepCounterType,
    SingleColorType,
    DirectionType,
    ComponentStageType,
} from '../types';

import { setBackgroundColor } from '../redux/actions/backgroundColor';
import { nextStep, previousStep } from '../redux/actions/step';

import { VisualAcuityTestView } from '../components/VisualAcuityTest';

import { colors } from '../constants';
import { pushToDataLayer } from '../utils';

interface IProps {
    subStepNumber: number;
    setBackgroundColorConnect(color: SingleColorType): void;
    onNextStep(stepCounter: StepCounterType): void;
    onPreviousStep(stepCounter: StepCounterType): void;
    onAnimationExit(): void;
}

interface IState {
    testStepNumber: number;
    componentStage: ComponentStageType;
    numberMistakes: number;
    openingDirection?: DirectionType;
    currentAnswer: boolean | null;
}

const testSizes = [80, 51.96, 33.72, 21.92, 14.24, 9.24, 6];

class BaseVisualAcuityTest extends React.Component<IProps, IState> {
    state: IState = {
        componentStage: 'enter',
        numberMistakes: 0,
        openingDirection: undefined,
        testStepNumber: 1,
        currentAnswer: null,
    };

    componentDidMount() {
        const { setBackgroundColorConnect } = this.props;
        setBackgroundColorConnect(colors.white);
        this.setRandomOpeningDirection();

        document.addEventListener('keydown', this.handleKeyPress);
    }

    componentDidUpdate(prevProps: IProps, prevState: IState) {
        if (prevState.testStepNumber !== this.state.testStepNumber) {
            this.setState({ currentAnswer: null });
        }
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyPress);
    }

    setRandomOpeningDirection = () => {
        const { openingDirection } = this.state;

        const directions: DirectionType[] = ['up', 'down', 'left', 'right'];

        if (openingDirection) {
            // prevent same result twice in a row
            const currentDirectionIndex = directions.indexOf(openingDirection);
            directions.splice(currentDirectionIndex, 1);
        }

        const randomInt = Math.floor(Math.random() * directions.length);

        this.setState({
            openingDirection: directions[randomInt],
        });
    };

    setComponentStageExit = () => {
        this.setState({
            componentStage: 'exit',
        });
    };

    handleKeyPress = (e: KeyboardEvent) => {
        type KeyMapping = {
            [key: string]: DirectionType;
        };

        const keyMappings: KeyMapping = {
            ArrowUp: 'up',
            ArrowRight: 'right',
            ArrowDown: 'down',
            ArrowLeft: 'left',
        };

        if (!Object.keys(keyMappings).includes(e.key)) return;

        this.handleArrowClick(keyMappings[e.key]);
    };

    handleArrowClick = (arrowDirection: DirectionType) => {
        const { openingDirection, numberMistakes, testStepNumber } = this.state;

        const isCorrectAnswer = arrowDirection === openingDirection;

        setTimeout(() => {
            if (!isCorrectAnswer && numberMistakes >= 1) {
                return this.setComponentStageExit();
            }
        }, 500);

        this.setState({ currentAnswer: isCorrectAnswer });

        if (!isCorrectAnswer) {
            this.setState((prevState) => ({
                numberMistakes: prevState.numberMistakes + 1,
            }));
        }

        this.handleDataLayerEvent(arrowDirection);

        setTimeout(() => {
            if (testStepNumber < testSizes.length) {
                this.setRandomOpeningDirection();

                this.setState({ testStepNumber: testStepNumber + 1 });

                return;
            }

            this.setComponentStageExit();
        }, 1500);
    };

    handlePassQuestionClick = () => {
        pushToDataLayer(
            '1: Geef aan waar de opening van de cirkel zit.',
            'Ik weet het niet'
        );

        this.setComponentStageExit();
    };

    handleDataLayerEvent = (arrowDirection: DirectionType) => {
        const directionTranslations = {
            up: 'boven',
            down: 'onder',
            left: 'links',
            right: 'rechts',
        };

        pushToDataLayer(
            '1: Geef aan waar de opening van de cirkel zit.',
            directionTranslations[arrowDirection]
        );
    };

    handleAnimationExit = () => {
        const { subStepNumber: totalSubStepNumber, onNextStep } = this.props;
        if (this.state.componentStage === 'exit') {
            totalSubStepNumber !== testSizes.length
                ? this.props.onAnimationExit()
                : onNextStep('main');
        }
    };

    render() {
        const { openingDirection, componentStage, testStepNumber } = this.state;

        const currentTestSizePx = testSizes[testStepNumber - 1];

        return (
            <VisualAcuityTestView
                currentTestSizePx={currentTestSizePx}
                currentAnswer={this.state.currentAnswer}
                openingDirection={openingDirection}
                componentStage={componentStage}
                onArrowClick={this.handleArrowClick}
                onPassQuestionClick={this.handlePassQuestionClick}
                onAnimationExit={this.handleAnimationExit}
            />
        );
    }
}

const mapStateToProps = (state: IReduxState) => ({
    subStepNumber: state.step.visualAcuity,
});

const mapDispatchToProps = {
    setBackgroundColorConnect: setBackgroundColor,
    onNextStep: nextStep,
    onPreviousStep: previousStep,
};

export const VisualAcuityTest = connect(
    mapStateToProps,
    mapDispatchToProps
)(BaseVisualAcuityTest);
